"use client"

import { Clock, LayoutGrid } from "lucide-react"

const SUMMARY_HEADERS = [
  { key: "totalItems", label: "Total Items" },
  { key: "highRunning", label: "High Running" },
  { key: "stockOutRisk", label: "Stock-Out Risk" },
  { key: "reorderRequired", label: "Reorder Required" },
  { key: "nonMoving", label: "Non-Moving" },
]

function AnalysisTable({ title, groupLabels, rows, rowKey, renderGroupCells }) {
  return (
    <div className="border border-gray-200 rounded-xl shadow-sm overflow-hidden bg-white">
      <div className="px-4 py-3 bg-gray-50/60 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
              {groupLabels.map((label) => (
                <th key={label} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                  {label}
                </th>
              ))}
              {SUMMARY_HEADERS.map((h) => (
                <th key={h.key} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={groupLabels.length + SUMMARY_HEADERS.length} className="px-4 py-8 text-center text-sm text-gray-500">
                  No data available.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={rowKey(row)} className="hover:bg-gray-50">
                  {renderGroupCells(row)}
                  {SUMMARY_HEADERS.map((h) => (
                    <td key={h.key} className="px-4 py-2.5 text-sm text-gray-700">{row[h.key]}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function InventoryAnalyticsAnalysisTab({ analysis, loading, error }) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="h-5 w-5 animate-spin text-[#0263cc]" />
          <span>Loading analysis...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center text-sm text-red-600">{error}</div>
    )
  }

  return (
    <div className="p-4 space-y-5 bg-gray-50/40">
      <div className="flex items-center gap-2 text-[#0263cc]">
        <LayoutGrid className="h-4 w-4" />
        <span className="text-sm font-medium">Movement &amp; risk breakdown across groupings</span>
      </div>

      <AnalysisTable
        title="Category × Sub-category"
        groupLabels={["Category", "Sub-category"]}
        rows={analysis.byCategorySubCategory}
        rowKey={(row) => `${row.category}||${row.sub_category}`}
        renderGroupCells={(row) => (
          <>
            <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{row.category || "—"}</td>
            <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{row.sub_category || "—"}</td>
          </>
        )}
      />

      <AnalysisTable
        title="Category × Brand"
        groupLabels={["Category", "Brand"]}
        rows={analysis.byCategoryBrand}
        rowKey={(row) => `${row.category}||${row.brand}`}
        renderGroupCells={(row) => (
          <>
            <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{row.category || "—"}</td>
            <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{row.brand || "—"}</td>
          </>
        )}
      />

      <AnalysisTable
        title="Category × Brand × SKU"
        groupLabels={["Category", "Brand", "Item"]}
        rows={analysis.byCategoryBrandSku}
        rowKey={(row) => `${row.category}||${row.brand}||${row.inventory_id}`}
        renderGroupCells={(row) => (
          <>
            <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{row.category || "—"}</td>
            <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{row.brand || "—"}</td>
            <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{row.item_name || "—"}</td>
          </>
        )}
      />
    </div>
  )
}
